import React, { Component, useEffect } from 'react';
import { InteractionManager, View } from 'react-native';
import { Provider } from 'react-redux';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';

import NetworkUtils from './networkUtils';
import { store } from './store';
import { env, ENV_NAME } from './constants/env';
const PART_A_ENABLED = env(ENV_NAME) !== 'production';

import { NEW_INVITE_NOTIFICATION } from './notificationsHandlersTypes';

import { appInitialized, handleBranchLinkLaunch } from './actions/actions';

import { getAssociatedEmployer } from './selectors/employers';

import I18n from './I18n.js';

import { OKTA_AUTH_SUCCESS } from './actions/actionTypes';

// require this so console.error gets overwritten with the datadog log method
require('./errors/logger');

import { getRoutes, getUnauthRoutes } from './screens/routes';
import { Navigation } from './screens/components';

import AuthedAppContainer from './AuthedAppContainer';
import UnauthedAppContainer from './UnauthedAppContainer';
import OktaAppContainer from './screens/auth/okta/app_container.web.js';
import BaseUnauthed from './screens/airforce/base_unauthed/main.js';
import PartA from './screens/parta/main';
import ModalProvider from './screens/ModalProvider';
import { setUser } from './errors/logger';

import Error404 from './screens/airforce/base_unauthed/error_404.web.js';
import LinkExpired from './screens/airforce/base_unauthed/link_expired.web.js';

NetworkUtils.setupInterceptors(store);

const DEFAULT_ROUTE = 'Home';
const ERROR_ROUTE = 'Error';
const DEFAULT_LOCALE = 'en-US';
const LINK_EXPIRED_PATH = '/link-expired';

import { init as initRDXB } from './rdxb/watchers';

let _inviteGuid = null;

function RouterConfigurer(props) {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(
    function () {
      props.onLocation?.(location);
      Navigation.setCurrentScreen(location.pathname);
    },
    [location.pathname]
  );

  useEffect(function () {
    Navigation.setNavigate(navigate);
    Navigation.setCurrentScreen(location.pathname);
  }, []);
}

function _userSelectedLocale() {
  const state = store.getState();
  // state.application.locale should always have a value and be
  // kept in sync with state.entities.userProfile
  // falling back on state.entities.userProfile just in case
  return state.application.locale || state.entities.userProfile?.locale || DEFAULT_LOCALE;
}

class App extends Component {
  constructor(props) {
    super(props);

    let base = false;
    const locationPath = window.location.pathname.toLowerCase();
    if (locationPath.match('base/')) {
      const baseParts = locationPath.split('base/')[1];
      base = baseParts?.split('/')?.[0];
    }
    let partA = false;
    let partAOneUseToken = null;
    let partAMode = null;
    if (PART_A_ENABLED && locationPath.toLowerCase().match('parta')) {
      partAOneUseToken = new URLSearchParams(location.search).get('token');
      partAMode = new URLSearchParams(location.search).get('mode');
      partA = true;
    }

    let internal = false;
    if (locationPath.match('internal')) {
      internal = true;
    }

    this.state = {
      authStatus: null,
      displayLocale: null,
      employerUIConfig: null,
      base,
      partA,
      partAOneUseToken,
      partAMode,
      internal,
    };

    this.authedRoutes = getRoutes();
    this.unauthedRoutes = getUnauthRoutes();

    initRDXB();
  }

  componentDidMount() {
    if (this.props.oktaToken) {
      // If we have an Okta token, we should set the user and update the auth status
      store.dispatch({
        type: OKTA_AUTH_SUCCESS,
        payload: { token: this.props.oktaToken.idToken, nonce: this.props.oktaToken.claims.nonce },
      });
    }
    this.initApp();
  }

  componentDidUpdate(prevProps, prevState) {
    const { authStatus } = this.state;

    if (this.props.oktaToken && !this.state.oktaAuthToken && prevState.oktaAuthToken) {
      this.props.onLogout?.();
    }

    // we just changed auth states, handle that now
    if (authStatus !== prevState.authStatus) {
      if (prevState.authStatus == null) {
        if (_inviteGuid && !this.props.oktaToken) {
          store.dispatch(
            handleBranchLinkLaunch({ resource: NEW_INVITE_NOTIFICATION, guid: _inviteGuid })
          );
          _inviteGuid = null;
        }
      }
      if (!authStatus) Navigation.dismissAllModals();

      // Make sure to navigate to the right screen when we switch auth states
      requestAnimationFrame(() => {
        let screen = window.location.pathname.substring(1);
        if (
          screen?.toLowerCase()?.startsWith('base') ||
          (PART_A_ENABLED && screen?.toLowerCase()?.startsWith('parta')) ||
          screen?.toLowerCase()?.startsWith('internal') ||
          screen?.toLowerCase() === LINK_EXPIRED_PATH.substring(1)
        )
          return;

        if (!screen || screen?.toLowerCase()?.startsWith('invite')) {
          Navigation.openScreen({ screen: DEFAULT_ROUTE });
          return;
        }
        if (authStatus && !this.authedRoutes[screen]) {
          Navigation.openScreen({ screen: DEFAULT_ROUTE });
          return;
        }
        if (!authStatus && !this.unauthedRoutes[screen]) {
          if (!!this.authedRoutes[screen]) {
            Navigation.openScreen({ screen: DEFAULT_ROUTE });
          } else {
            Navigation.openScreen({ screen: ERROR_ROUTE });
          }
          return;
        }
      });

      // Initialize the app if we haven't yet
      if (authStatus) {
        setUser(store.getState().user);
        store.dispatch(appInitialized());
      } else {
        const { intentToResetPassword } = store.getState().screens.auth;
        if (intentToResetPassword) {
          InteractionManager.runAfterInteractions(() => {
            setTimeout(() => {
              Navigation.openScreen({ screen: 'ForgotPassword' }, this.props.componentId, false);
            }, 10);
          });
        }
      }
    }
  }

  onStoreUpdate = () => {
    const state = store.getState();

    // check to see if that authStatus changes
    const authStatus = !!state.user.userId && !state.user.pendingTermsAccept;
    const displayLocale = _userSelectedLocale();
    const employerUIConfig = getAssociatedEmployer(store.getState())?.uiConfig;

    // set locale and ui config on I18n before triggering the state change so the updates
    // are applied on the next render
    if (displayLocale !== this.state.displayLocale) {
      I18n.setPreferredLocale(displayLocale);
    }
    if (employerUIConfig !== this.state.employerUIConfig) {
      I18n.setEmplopyerUIConfig(employerUIConfig);
    }

    this.setState({
      displayLocale,
      employerUIConfig,
      authStatus,
      oktaAuthToken: state.user.oktaAuthToken,
    });

    const baseInfoError = state.screens.baseUnauthed.error;
    if (
      baseInfoError?.response?.httpStatusCode === 400 &&
      baseInfoError?.response?.data?.errorCode === 3 &&
      this.state.pathName !== '/Error'
    ) {
      Navigation.openScreen({ screen: ERROR_ROUTE });
    }
  };

  initApp = () => {
    store.subscribe(this.onStoreUpdate);
    this.onStoreUpdate();
    // for web just use a basic route to launch from an invite. the link in the invite email will
    // forward to this route
    const locationPath = window.location.pathname.toLowerCase();
    if (locationPath.startsWith('/invite/')) {
      _inviteGuid = locationPath.split('/invite/')[1];
    }
  };

  handleLocationChange = (location) => {
    this.setState({ pathName: location.pathname });
  };

  render() {
    const appCompId =
      this.state.pathName?.toLowerCase() === LINK_EXPIRED_PATH
        ? 'link-expired'
        : this.state.pathName === '/Error'
          ? 'error'
          : this.state.partA
            ? 'parta'
            : this.state.base
              ? 'base'
              : this.state.authStatus
                ? 'authed'
                : this.props.oktaToken
                  ? 'okta'
                  : 'unauthed';

    return (
      <Provider store={store}>
        <BrowserRouter>
          <RouterConfigurer onLocation={this.handleLocationChange} />
          {this.state.authStatus != null && (
            <View style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
              <ModalProvider appCompId={appCompId}>
                <View style={{ flex: 1, overflow: 'auto' }}>
                  {appCompId === 'link-expired' ? (
                    <LinkExpired />
                  ) : appCompId === 'error' ? (
                    <Error404 slug={this.state.base} />
                  ) : appCompId === 'parta' ? (
                    <PartA
                      oneUseToken={this.state.partAOneUseToken}
                      initialMode={this.state.partAMode}
                    />
                  ) : appCompId === 'base' ? (
                    <BaseUnauthed base={this.state.base} userData={this.props.userData} />
                  ) : appCompId === 'authed' ? (
                    <AuthedAppContainer pathName={this.state.pathName} />
                  ) : appCompId === 'okta' ? (
                    <OktaAppContainer />
                  ) : (
                    <UnauthedAppContainer pathName={this.state.pathName} />
                  )}
                </View>
              </ModalProvider>
            </View>
          )}
        </BrowserRouter>
      </Provider>
    );
  }
}

export default App;
